import { Inject, Injectable } from "@angular/core";
import { filter, map, Observable, Subject } from "rxjs";

import { BaseSignalRClass } from "@app/core/signalR/base-signalr.class";
import {
  EBrowserNotifyType,
  TDocumentStatusChanged,
  TJournalEntryImportedEvent,
} from "@app/core/signalR/browser-notify.type";
import { ISignalRConfig } from "@app/core/signalR/signalr-config.interface";
import { NOTIFY_HUB_CONFIG } from "@app/core/signalR/signalr-injections";
import { EEntityTypeName } from "@app/shared/enums/entity-type.enum";
import { EDocumentGenerationStatus } from "@app/shared/types/policy-document.type";

export interface TNotification {
  id: string;
  type: EBrowserNotifyType;
  title: string;
  attribute: Record<string, unknown>;
  payload?: unknown;
}

export interface TBankStatementImportedEvent {
  requestId: string;
  importId: string;
  lineCount?: number;
  error?: string;
}

const DOCUMENT_GENERATION_STATUSES: readonly string[] = Object.values(EDocumentGenerationStatus);
const ENTITY_TYPE_NAMES: readonly string[] = Object.values(EEntityTypeName);

// Messages that drive a specific UI and never belong in the bell.
const SILENT_NOTIFICATION_TYPES: readonly EBrowserNotifyType[] = [
  EBrowserNotifyType.DocumentStatusChanged,
  EBrowserNotifyType.JournalEntryImported,
];

@Injectable({ providedIn: "root" })
export class NotificationHubService extends BaseSignalRClass {
  private _newNotification$ = new Subject<TNotification>();
  private _bankStatementImported$ = new Subject<TBankStatementImportedEvent>();

  readonly newNotification$ = this._newNotification$.asObservable();

  readonly bellNotification$: Observable<TNotification> = this.newNotification$.pipe(
    filter(notification => !SILENT_NOTIFICATION_TYPES.includes(notification.type)),
  );

  readonly documentStatusChanged$: Observable<TDocumentStatusChanged> = this.newNotification$.pipe(
    filter(notification => notification.type === EBrowserNotifyType.DocumentStatusChanged),
    map(notification => this.toDocumentStatusChanged(notification.payload)),
    filter((change): change is TDocumentStatusChanged => change !== null),
  );

  readonly journalEntryImported$: Observable<TJournalEntryImportedEvent> = this.newNotification$.pipe(
    filter(notification => notification.type === EBrowserNotifyType.JournalEntryImported),
    map(notification => this.toJournalEntryImported(notification.payload)),
    filter((event): event is TJournalEntryImportedEvent => event !== null),
  );

  readonly bankStatementImported$ = this._bankStatementImported$.asObservable();

  constructor(@Inject(NOTIFY_HUB_CONFIG) config: ISignalRConfig) {
    super(config.hubUrl, config.logLevel, config.accessTokenFactory);
  }

  override connection() {
    super.connection();

    this.registerEvents();
  }

  private registerEvents(): void {
    if (!this.hubConnection) {
      console.warn("Hub connection is not initialized. Call connection() first.");
      return;
    }

    this.hubConnection.on("Notify", (data: TNotification) => {
      this._newNotification$.next(data);
    });

    this.hubConnection.on("BankStatementImported", (data: TBankStatementImportedEvent) => {
      this._bankStatementImported$.next(data);
    });
  }

  private toDocumentStatusChanged(payload: unknown): TDocumentStatusChanged | null {
    if (typeof payload !== "object" || payload === null) {
      return null;
    }

    const { documentFileId, status, fileUri, entityType, entityId, policyId, clientId } =
      payload as Record<string, unknown>;

    if (typeof documentFileId !== "string" || typeof status !== "string") {
      return null;
    }

    if (!DOCUMENT_GENERATION_STATUSES.includes(status)) {
      return null;
    }

    return {
      documentFileId,
      status: status as EDocumentGenerationStatus,
      fileUri: this.toNullableString(fileUri),
      entityType: this.toEntityTypeName(entityType),
      entityId: this.toNullableString(entityId),
      policyId: this.toNullableString(policyId),
      clientId: this.toNullableString(clientId),
    };
  }

  private toJournalEntryImported(payload: unknown): TJournalEntryImportedEvent | null {
    if (typeof payload !== "object" || payload === null) {
      return null;
    }

    const { requestId, importId, error, errors, isSuccess } = payload as Record<string, unknown>;

    if (typeof requestId !== "string") {
      return null;
    }

    return {
      requestId,
      importId: typeof importId === "string" ? importId : "",
      error: typeof error === "string" ? error : null,
      errors: Array.isArray(errors) ? errors.filter((e): e is string => typeof e === "string") : [],
      isSuccess: isSuccess === true,
    };
  }

  private toNullableString(value: unknown): string | null {
    return typeof value === "string" ? value : null;
  }

  private toEntityTypeName(value: unknown): EEntityTypeName | null {
    return typeof value === "string" && ENTITY_TYPE_NAMES.includes(value) ? (value as EEntityTypeName) : null;
  }
}
